import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, Calendar, Repeat, BookOpen, Package, Zap, Trash2, Plus, AlertTriangle, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function auth() {
  const t = localStorage.getItem('bar_owner_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const SECTIONS = [
  { id: 'staff', label: 'Staff', Icon: Users },
  { id: 'schedule', label: 'Schedule', Icon: Calendar },
  { id: 'trades', label: 'Trades', Icon: Repeat },
  { id: 'menu', label: 'Menu', Icon: BookOpen },
  { id: 'inventory', label: 'Inventory', Icon: Package },
  { id: 'predict', label: 'Rush Predict', Icon: Zap },
];

export default function ShiftHub() {
  const [active, setActive] = useState('staff');
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [trades, setTrades] = useState([]);
  const [menu, setMenu] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [predict, setPredict] = useState([]);

  // Forms
  const [newStaff, setNewStaff] = useState({ name: '', role: 'bartender', phone: '' });
  const [newShift, setNewShift] = useState({ staff_id: '', start_at: '', end_at: '', role: '' });
  const [newMenu, setNewMenu] = useState({ name: '', price: '', category: '' });
  const [newInv, setNewInv] = useState({ name: '', current_stock: '', low_stock_threshold: '', unit: 'bottles' });

  const load = async () => {
    try {
      const [s, sh, tr, m, i] = await Promise.all([
        axios.get(`${API}/shift-hub/staff`, { headers: auth() }),
        axios.get(`${API}/shift-hub/shifts`, { headers: auth() }),
        axios.get(`${API}/shift-hub/trades`, { headers: auth() }),
        axios.get(`${API}/shift-hub/menu`, { headers: auth() }),
        axios.get(`${API}/shift-hub/inventory`, { headers: auth() }),
      ]);
      setStaff(s.data.staff || []);
      setShifts(sh.data.shifts || []);
      setTrades(tr.data.trades || []);
      setMenu(m.data.items || []);
      setInventory(i.data.items || []);
    } catch {/* not owner logged in yet */}
  };

  const loadPredict = async () => {
    try {
      const r = await axios.get(`${API}/shift-hub/predict/rush`, { headers: auth() });
      setPredict(r.data.predictions || []);
    } catch {/* ignore */}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (active === 'predict') loadPredict(); }, [active]);

  const staffName = (id) => staff.find((s) => s.id === id)?.name || id.slice(0, 6);

  const addStaff = async () => {
    if (!newStaff.name.trim()) return toast.error('Name required');
    try {
      await axios.post(`${API}/shift-hub/staff`, newStaff, { headers: auth() });
      setNewStaff({ name: '', role: 'bartender', phone: '' });
      toast.success('Staff added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const delStaff = async (id) => {
    if (!window.confirm('Remove this staff member?')) return;
    await axios.delete(`${API}/shift-hub/staff/${id}`, { headers: auth() });
    load();
  };

  const addShift = async () => {
    if (!newShift.staff_id || !newShift.start_at || !newShift.end_at) return toast.error('Fill all fields');
    try {
      await axios.post(`${API}/shift-hub/shifts`, {
        staff_id: newShift.staff_id,
        start_at: new Date(newShift.start_at).toISOString(),
        end_at: new Date(newShift.end_at).toISOString(),
        role: newShift.role,
      }, { headers: auth() });
      setNewShift({ staff_id: '', start_at: '', end_at: '', role: '' });
      toast.success('Shift scheduled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const delShift = async (id) => {
    await axios.delete(`${API}/shift-hub/shifts/${id}`, { headers: auth() });
    load();
  };

  const decideTrade = async (id, decision) => {
    try {
      await axios.put(`${API}/shift-hub/trades/${id}/${decision}`, {}, { headers: auth() });
      toast.success(`Trade ${decision}d`);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const addMenu = async () => {
    if (!newMenu.name || !newMenu.price) return toast.error('Name + price required');
    if (menu.length >= 50) return toast.error('Menu is at 50-item limit');
    try {
      await axios.post(`${API}/shift-hub/menu`, {
        name: newMenu.name, price: parseFloat(newMenu.price), category: newMenu.category,
      }, { headers: auth() });
      setNewMenu({ name: '', price: '', category: '' });
      toast.success('Item added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const delMenu = async (id) => {
    await axios.delete(`${API}/shift-hub/menu/${id}`, { headers: auth() });
    load();
  };

  const addInv = async () => {
    if (!newInv.name) return toast.error('Name required');
    try {
      await axios.post(`${API}/shift-hub/inventory`, {
        name: newInv.name,
        current_stock: parseFloat(newInv.current_stock || 0),
        low_stock_threshold: parseFloat(newInv.low_stock_threshold || 0),
        unit: newInv.unit,
      }, { headers: auth() });
      setNewInv({ name: '', current_stock: '', low_stock_threshold: '', unit: 'bottles' });
      toast.success('Added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const updateInvStock = async (id, current_stock) => {
    await axios.put(`${API}/shift-hub/inventory/${id}`, { current_stock: parseFloat(current_stock) }, { headers: auth() });
    load();
  };

  const delInv = async (id) => {
    await axios.delete(`${API}/shift-hub/inventory/${id}`, { headers: auth() });
    load();
  };

  return (
    <Card data-testid="shift-hub" className="glass p-4 border border-slate-700/50 space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-indigo-400" />
        <h3 className="text-lg font-bold text-white">Shift Hub</h3>
      </div>

      {/* Sub-tab nav */}
      <div className="flex flex-wrap gap-1 border-b border-slate-700 pb-2">
        {SECTIONS.map(({ id, label, Icon }) => (
          <button
            key={id}
            data-testid={`shifthub-tab-${id}`}
            onClick={() => setActive(id)}
            className={`px-3 py-1.5 rounded-full text-xs font-mono flex items-center gap-1 transition ${
              active === id ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <Icon className="w-3 h-3" /> {label}
          </button>
        ))}
      </div>

      {/* STAFF */}
      {active === 'staff' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input data-testid="staff-name-input" placeholder="Name" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Role (bartender, server…)" value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <div className="flex gap-2">
              <Input placeholder="Phone (optional)" value={newStaff.phone} onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
              <Button data-testid="staff-add-btn" onClick={addStaff} className="bg-indigo-600 hover:bg-indigo-500"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="space-y-1">
            {staff.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-slate-800/50 rounded p-2">
                <span className="text-sm text-white">{s.name} <span className="text-xs text-slate-400">· {s.role}</span></span>
                <button onClick={() => delStaff(s.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!staff.length && <p className="text-xs text-slate-500 text-center py-3">No staff yet — add your bartenders above.</p>}
          </div>
        </div>
      )}

      {/* SCHEDULE */}
      {active === 'schedule' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <select data-testid="shift-staff-select" value={newShift.staff_id} onChange={(e) => setNewShift({ ...newShift, staff_id: e.target.value })} className="bg-slate-800 border border-slate-700 text-white rounded px-2 py-2 text-sm">
              <option value="">Pick staff…</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Input type="datetime-local" value={newShift.start_at} onChange={(e) => setNewShift({ ...newShift, start_at: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input type="datetime-local" value={newShift.end_at} onChange={(e) => setNewShift({ ...newShift, end_at: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Button data-testid="shift-add-btn" onClick={addShift} className="bg-indigo-600 hover:bg-indigo-500">Schedule</Button>
          </div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {shifts.map((s) => (
              <div key={s.id} className="flex items-center justify-between bg-slate-800/50 rounded p-2 text-xs">
                <div>
                  <p className="text-white font-semibold">{staffName(s.staff_id)}</p>
                  <p className="text-slate-400">{new Date(s.start_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} → {new Date(s.end_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <button onClick={() => delShift(s.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!shifts.length && <p className="text-xs text-slate-500 text-center py-3">No shifts scheduled.</p>}
          </div>
        </div>
      )}

      {/* TRADES */}
      {active === 'trades' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">Bartender-initiated trades must be approved here before they take effect.</p>
          {trades.map((t) => (
            <div key={t.id} data-testid={`trade-${t.id}`} className="bg-slate-800/50 rounded p-2 flex items-center justify-between">
              <div className="text-xs">
                <p className="text-white">{staffName(t.from_staff_id)} → {staffName(t.to_staff_id)}</p>
                <p className="text-slate-400">Reason: {t.reason || '—'} · <Badge className={
                  t.status === 'pending' ? 'bg-yellow-600' : t.status === 'approved' ? 'bg-emerald-600' : 'bg-red-700'
                }>{t.status.toUpperCase()}</Badge></p>
              </div>
              {t.status === 'pending' && (
                <div className="flex gap-1">
                  <Button data-testid={`approve-trade-${t.id}`} size="sm" onClick={() => decideTrade(t.id, 'approve')} className="bg-emerald-600 hover:bg-emerald-500"><Check className="w-3 h-3" /></Button>
                  <Button data-testid={`reject-trade-${t.id}`} size="sm" onClick={() => decideTrade(t.id, 'reject')} className="bg-red-600 hover:bg-red-500"><X className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          ))}
          {!trades.length && <p className="text-xs text-slate-500 text-center py-3">No trade requests yet.</p>}
        </div>
      )}

      {/* MENU */}
      {active === 'menu' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">{menu.length}/50 items used</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Input placeholder="Item name" value={newMenu.name} onChange={(e) => setNewMenu({ ...newMenu, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Price" type="number" step="0.01" value={newMenu.price} onChange={(e) => setNewMenu({ ...newMenu, price: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Category" value={newMenu.category} onChange={(e) => setNewMenu({ ...newMenu, category: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Button data-testid="menu-add-btn" onClick={addMenu} className="bg-indigo-600 hover:bg-indigo-500">Add</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
            {menu.map((m) => (
              <div key={m.id} className="flex items-center justify-between bg-slate-800/50 rounded p-2 text-xs">
                <div>
                  <p className="text-white font-semibold">{m.name} — ${m.price?.toFixed(2)}</p>
                  <p className="text-slate-400">{m.category}</p>
                </div>
                <button onClick={() => delMenu(m.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* INVENTORY */}
      {active === 'inventory' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            <Input placeholder="Item name" value={newInv.name} onChange={(e) => setNewInv({ ...newInv, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Stock" type="number" value={newInv.current_stock} onChange={(e) => setNewInv({ ...newInv, current_stock: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Low @" type="number" value={newInv.low_stock_threshold} onChange={(e) => setNewInv({ ...newInv, low_stock_threshold: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Unit" value={newInv.unit} onChange={(e) => setNewInv({ ...newInv, unit: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Button data-testid="inv-add-btn" onClick={addInv} className="bg-indigo-600 hover:bg-indigo-500">Add</Button>
          </div>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {inventory.map((i) => (
              <div key={i.id} className={`flex items-center justify-between rounded p-2 text-xs ${i.is_low ? 'bg-red-900/30 border border-red-500/40' : 'bg-slate-800/50'}`}>
                <div className="flex-1">
                  <p className="text-white font-semibold flex items-center gap-1">
                    {i.name} {i.is_low && <AlertTriangle className="w-3 h-3 text-red-400" />}
                  </p>
                  <p className="text-slate-400">Threshold: {i.low_stock_threshold} {i.unit}</p>
                </div>
                <Input
                  type="number"
                  defaultValue={i.current_stock}
                  onBlur={(e) => updateInvStock(i.id, e.target.value)}
                  className="w-20 bg-slate-800 border-slate-700 text-white text-center"
                />
                <button onClick={() => delInv(i.id)} className="text-red-400 ml-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PREDICT */}
      {active === 'predict' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">Heuristic: if current-hour check-ins beat the 7-day average by 40%+, we recommend clocking staff in 30 min early.</p>
          {predict.map((p) => (
            <div key={p.bar_id} data-testid={`predict-${p.bar_id}`} className={`rounded p-3 border ${p.packed ? 'bg-yellow-900/20 border-yellow-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
              <p className="text-sm font-bold text-white flex items-center gap-1">
                {p.packed && <Zap className="w-4 h-4 text-yellow-300" />}
                {p.bar_name}
              </p>
              <p className="text-xs text-slate-300">Current: {p.current_hour_checkins} · Avg: {p.avg_hourly_checkins}/hr</p>
              <p className={`text-xs mt-1 font-mono ${p.packed ? 'text-yellow-300' : 'text-slate-400'}`}>{p.action}</p>
            </div>
          ))}
          {!predict.length && <p className="text-xs text-slate-500 text-center py-3">Waiting for check-in data on your bars…</p>}
        </div>
      )}
    </Card>
  );
}
